import { useMemo } from 'react';
import {
  ArrowUpRight,
  BarChart3,
  CalendarDays,
  FileKey,
  Package,
  Shield,
  Sparkles,
  Star,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import type { MarketplaceMembership } from '@/hooks/supabase/useMarketplaceMemberships';
import type { AdminPanel } from '@/components/maple-market/admin/AdminSidebar';

interface DashboardPanelProps {
  products: MapleMarketApp[];
  memberships: MarketplaceMembership[];
  isSiteAdmin: boolean;
  onOpenProduct: (productId: string) => void;
  onSelectPanel: (panel: AdminPanel) => void;
}

export default function DashboardPanel({
  products,
  memberships,
  isSiteAdmin,
  onOpenProduct,
  onSelectPanel,
}: DashboardPanelProps) {
  const averageRating = useMemo(() => {
    if (products.length === 0) {
      return 0;
    }

    return products.reduce((total, product) => total + product.rating, 0) / products.length;
  }, [products]);

  const topProducts = useMemo(
    () => [...products].sort((left, right) => right.rating - left.rating).slice(0, 4),
    [products]
  );

  const pendingPublishRequests = useMemo(
    () => products.filter((product) => product.publishRequestStatus === 'pending'),
    [products],
  );

  const statCards = [

    {
      title: 'Accessible apps',
      value: products.length,
      description: isSiteAdmin ? 'All marketplace products' : 'Products assigned to your account',
      icon: Package,
    },
    {
      title: 'Featured apps',
      value: products.filter((product) => product.featured).length,
      description: 'Currently promoted in Maple Market',
      icon: Sparkles,
    },
    {
      title: 'Pending reviews',
      value: pendingPublishRequests.length,
      description: isSiteAdmin ? 'Publish requests waiting for admin review' : 'Your products awaiting a decision',
      icon: Shield,
    },
    {
      title: 'Average rating',
      value: averageRating ? averageRating.toFixed(1) : '0.0',
      description: 'Live review average across accessible apps',
      icon: Star,
    },
  ];

  const toolkitItems: Array<{ label: string; panel: AdminPanel; icon: typeof BarChart3 }> = [
    { label: 'Analytics dashboards', panel: 'analytics', icon: BarChart3 },
    { label: 'Revenue reporting', panel: 'revenue', icon: Sparkles },
    { label: 'Publishing queue', panel: 'events', icon: CalendarDays },
    { label: 'Maple Keys', panel: 'keys', icon: FileKey },
  ];

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Marketplace overview</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage applications, publication review, visibility, Maple Keys, and team access without leaving MapleOS.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardDescription>{card.title}</CardDescription>
                <CardTitle className="mt-2 text-3xl">{card.value}</CardTitle>
              </div>
              <div className="rounded-xl bg-primary/10 p-2 text-primary">
                <card.icon className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Top applications</CardTitle>
            <CardDescription>Quick access to your strongest live listings.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {topProducts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                No products are available for this account yet.
              </div>
            ) : (
              topProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-4 rounded-xl border border-border p-3 transition-colors hover:bg-muted/40"
                >
                  <img src={product.image} alt={product.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium text-foreground">{product.name}</p>
                      {product.featured && <Badge variant="secondary">Featured</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{product.developer}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-foreground">★ {product.rating.toFixed(1)}</div>
                    <div className="text-xs text-muted-foreground">{product.reviews.toLocaleString()} reviews</div>
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => onOpenProduct(product.id)}>
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Publishing queue</CardTitle>
              <CardDescription>Pending requests and hidden inventory needing attention.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingPublishRequests.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No publish requests are waiting right now.
                </div>
              ) : (
                pendingPublishRequests.map((product) => (
                  <div key={product.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{product.name}</div>
                      <div className="text-sm text-muted-foreground">Requested {product.publishRequestedAt ? new Date(product.publishRequestedAt).toLocaleString() : 'recently'}</div>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => onOpenProduct(product.id)}>
                      Review
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Admin toolkit</CardTitle>
              <CardDescription>These sections are now ready to use from inside MapleOS.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {toolkitItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => onSelectPanel(item.panel)}
                  className="flex w-full items-center gap-3 rounded-lg border border-border px-3 py-3 text-left text-sm transition-colors hover:bg-muted/40"
                >
                  <item.icon className="h-4 w-4 text-primary" />
                  <span className="flex-1">{item.label}</span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Access model</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                <span className="font-medium text-foreground">Site admins</span> can review every publish request and change any visibility state.
              </p>
              <p>
                <span className="font-medium text-foreground">Owners</span> submit first-time publication requests from MapleOS Pro builds.
              </p>
              <p>
                <span className="font-medium text-foreground">Existing owners</span> keep access to hidden and unpublished products through their library.
              </p>
              <p>
                <span className="font-medium text-foreground">Team memberships</span>: {memberships.length.toLocaleString()} scoped assignments across your accessible products.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
